import { DAYS_OBJECT, LANGUAGES } from '@/utils/consts';
import { hPrepareAndArrangeTheDaysReceivedFromApi, hFixDaysOrder, hDeepCopy } from '@/utils/helpers';
import moment from 'moment';
import IvrSingleMessageItem from '@/components/Projects/Common/IvrSingleMessageItem';

export default {
  name: 'HolidayOperationForm',
  components: {
    IvrSingleMessageItem,
  },
  props: {
    projectUuid: {
      required: false,
      type: String,
    },
    unit: {
      required: false,
      default: (() => {
      }),
    },
  },

  data() {
    return {
      languagesList: LANGUAGES,
      isAutocompletedLoading: false,
      elements: [],
      globalActions: [
        { value: 'permanent', label: 'Cykliczne' },
        { value: 'specific_day', label: 'W konkretnym dniu' },
        { value: 'regular', label: 'Okresowe' },
      ],
      workDays: DAYS_OBJECT,
      holiday: {
        element: '',
        element_id: '',
        work_day: '',
        holiday_time_begin: null,
        holiday_time_end: null,
        date: null,
        date_start: null,
        description: '',
        type: '',
        replacement_agent_id: '',
        lang: '',
        holidays_voice_text: '',
        holidays_voice: '',
      },
      rules: {
        element_id: [{required: true, message: 'Pole wymagane', trigger: 'blur'}]
      },
      step: 1,
    };
  },

  async created() {
    this.$store.dispatch('client/project/mainMenu/getList', this.projectUuid);
    await this.$store.dispatch("client/groups/getGroupsList");
    if (this.unit === undefined) {
      await this.getAgentList();
      await this.getAlternativeMenuList();
      this.prepareElementsMap();
    } else {
      this.fullFillEditorWithData();
      this.step = 2;
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters['global/pendingAsyncStoreActions'].includes([
        'client/project/agents/getList',
        'client/project/alternativeMenu/getList',
      ]);
    },
    projectName() {
      return this.$store.getters['client/project/details']?.name;
    },
    projectId() {
      return this.$store.getters['client/project/details']?.id;
    },
    agentsWithoutMainAgent() {
      return this.holiday.element === 'agent' && this.holiday.element_id
        ? hDeepCopy(this.agentList).filter(agent => agent.id !== this.holiday.element_id)
        : [];
    },
    projectAlternativeMenuList() {
      return this.$store.getters['client/project/alternativeMenu/list'].records;
    },
    menuElementsForStatement() {
      return this.projectAlternativeMenuList.filter(el => el.action === 5 && el.voice_introduction);
    },
    mainMenuList() {
      return this.$store.getters['client/project/mainMenu/list'].records;
    },
    agentList() {
      if (this.holiday.element === 'agent' && this.$store.getters['authorization/acl'].hasPermission('holidays_show_only_group_agents')) {
        return this.$store.getters['client/project/agents/list'].records.filter((item) => {
          return this.$store.getters["client/groups/list"].some(group => group.id === item.group_agents);
        });
      }
      return this.$store.getters['client/project/agents/list'].records;
    },
    pickerOptions() {
      return {
        firstDayOfWeek: 1,
      }
    },
    projectDetails() {
      return this.$store.getters['client/project/details'];
    },
    disableNextStepButton() {
      if ((this.holiday.element === 'komunikat' || this.holiday.element === 'agent' || this.holiday.element === 'menu_element') && this.holiday.element_id === ''){
        return true;
      } else {
        return false;
      }
    }
  },

  methods: {
    fullFillEditorWithData() {
      this.holiday = this.unit;
      if (this.unit.type !== 'specific_day' && !Array.isArray(this.unit.work_day)) {
        this.holiday.work_day = hPrepareAndArrangeTheDaysReceivedFromApi(this.unit.work_day);
      }
    },
    prepareElementsMap() {
      this.elements.push({ value: 'project', label: `Projekt ${this.projectName}` });
      this.elements.push({ value: 'komunikat', label: 'Komunikat' });

      if (this.agentList.length !== 0) {
        this.elements.push({ value: 'agent', label: 'Agenci' });
      }
      if (this.projectAlternativeMenuList.length !== 0) {
        this.elements.push({ value: 'menu_element', label: 'Elementy menu' });
        this.elements.push({ value: 'menu_b', label: 'Menu alternatywne' });
      }
    },
    async handleMessageElement(params) {
      await this.$store.dispatch('client/project/holidays/updateHoliday', {
        holiday: this.unit.uuid,
        ...params,
      });
      this.$message({
        type: 'success',
        message: 'Komunikat został zmieniony',
      });

      this.$emit('refreshList');
      this.closeModal();
    },
    fixDaysOrderAfterSelect() {
      this.holiday.work_day = hFixDaysOrder(this.holiday.work_day);
    },
    async getAgentList() {
      await this.$store.dispatch('client/project/agents/getList', { project: this.projectUuid });
    },
    async getAlternativeMenuList() {
      await this.$store.dispatch('client/project/alternativeMenu/getList', this.projectUuid);
    },
    nextStep() {
      this.step += 1;
    },
    previousStep() {
      this.step -= 1;
      this.$emit('previousStep');
    },
    clearParams() {
      this.holiday = {
        element: '',
      };
    },
    clearReplacementAgent() {
      this.holiday.replacement_agent_id = '';
    },
    async submitForm() {
      if (this.unit === undefined) {
        let params = {
          element: this.holiday.element,
          project: this.projectUuid,
          element_id: this.holiday.element_id,
          description: this.holiday.description,
          type: this.holiday.type,
          lang: this.holiday.lang,
          holidays_voice_text: this.holiday.holidays_voice_text,
          holidays_voice: this.holiday.holidays_voice,
          replacement_agent_id: this.holiday.replacement_agent_id,
        };
        const paramsToCreate = {
          ...params,
          ...(this.holiday.holiday_time_begin && { holiday_time_begin: moment(this.holiday.holiday_time_begin, 'HH:mm:ss').format('HH:mm:ss') }),
          ...(this.holiday.holiday_time_end && { holiday_time_end: moment(this.holiday.holiday_time_end, 'HH:mm:ss').format('HH:mm:ss') }),
          ...(this.holiday.work_day.length > 0 && { work_day: this.holiday.work_day }),
          ...(this.holiday.date && { date: moment(this.holiday.date, 'YYYY-MM-DD').format('YYYY-MM-DD') }),
          ...(this.holiday.date_start && { date_start: moment(this.holiday.date_start, 'YYYY-MM-DD').format('YYYY-MM-DD') }),
          ...(this.holiday.type === 'permanent' && { pernament: true }),
        };

        if (this.holiday.element === 'project' || this.holiday.element === 'menu_b') {
          paramsToCreate.element_id = this.projectId;
        }
        await this.$store.dispatch('client/project/holidays/createHoliday', paramsToCreate);
        this.$message({
          type: 'success',
          message: 'Tworzenie święta przebiegło pomyślnie',
        });
      } else {
        const paramsToUpdate = {
          ...(this.unit.work_day && this.unit.work_day.length > 0 && { work_day: this.unit.work_day }),
          ...(this.unit.date && { date: moment(this.unit.date, 'YYYY-MM-DD').format('YYYY-MM-DD') }),
          ...(this.unit.date_start && { date_start: moment(this.unit.date_start, 'YYYY-MM-DD').format('YYYY-MM-DD') }),
          ...(this.unit.holiday_time_begin && { holiday_time_begin: moment(this.unit.holiday_time_begin, 'HH:mm:ss').format('HH:mm:ss') }),
          ...(this.unit.holiday_time_end && { holiday_time_end: moment(this.unit.holiday_time_end, 'HH:mm:ss').format('HH:mm:ss') }),
        };
        await this.$store.dispatch('client/project/holidays/updateHoliday', {
          ...paramsToUpdate,
          holiday: this.unit.uuid,
        });
        this.$message({
          type: 'success',
          message: 'Edycja święta przebiegła pomyślnie',
        });
      }
      // pobranie listy świąt
      this.$emit('refreshList');
      this.closeModal();
    },
    closeModal() {
      this.$emit('closeModal');
    },
    setDefaultDaysInterval() {
      // eslint-disable-next-line no-unused-expressions
      this.holiday.type === 'permanent' ? this.holiday.work_day = [1, 2, 3, 4, 5, 6, 0] : this.holiday.work_day = '';
    },
  },
};
